/**
 * Map projection functions
 * Implements: Mercator, Miller, Lambert, Equirectangular
 *
 * Ported from Python (map_projection/projections.py)
 */

// Helper functions for angle conversion
function degToRad(degrees) {
    return degrees * Math.PI / 180;
}

function radToDeg(radians) {
    return radians * 180 / Math.PI;
}

/**
 * Mercator projection
 * @param {number} latitude - Latitude in radians
 * @param {number} longitude - Longitude in radians
 * @param {number} clipLat - Latitude clipping threshold (degrees)
 * @returns {Array<number>} [x, y] normalized to [0, 1]
 */
export function mercator(latitude, longitude, clipLat = 5) {
    // Convert longitude to x coordinate [0, 1]
    const x = (radToDeg(longitude) + 180) / 360;

    // Clip latitude to avoid pole singularities
    const latMin = -(90 - clipLat) * Math.PI / 180;
    const latMax = (90 - clipLat) * Math.PI / 180;
    const y_clipped = Math.max(latMin, Math.min(latMax, latitude));

    // Apply Mercator formula
    let y = Math.log(Math.tan(y_clipped / 2 + Math.PI / 4));

    // Rescale to [0, 1] using constants from Python
    y = (y + 3.1313013314716462) / 6.262602662943291;

    return [x, y];
}

/**
 * Inverse Mercator projection
 * @param {number} x - X coordinate [0, 1]
 * @param {number} y - Y coordinate [0, 1]
 * @returns {Array<number>} [latitude, longitude] in radians
 */
export function mercatorInverse(x, y) {
    // Convert x back to longitude
    const longitude = degToRad(x * 360 - 180);

    // Reverse the rescaling
    y = y * 6.262602662943291 - 3.1313013314716462;

    // Inverse Mercator formula
    const latitude = Math.atan(Math.sinh(y));

    return [latitude, longitude];
}

/**
 * Lambert cylindrical equal-area projection
 * @param {number} latitude - Latitude in radians
 * @param {number} longitude - Longitude in radians
 * @returns {Array<number>} [x, y] normalized to [0, 1]
 */
export function lambert(latitude, longitude) {
    const x = (radToDeg(longitude) + 180) / 360;
    const y = (radToDeg(Math.sin(latitude) * Math.PI / 2) + 90) / 180;
    return [x, y];
}

/**
 * Inverse Lambert projection
 * @param {number} x - X coordinate [0, 1]
 * @param {number} y - Y coordinate [0, 1]
 * @returns {Array<number>} [latitude, longitude] in radians
 */
export function lambertInverse(x, y) {
    const longitude = degToRad(x * 360 - 180);

    // Reverse Lambert formula: y = (degrees(sin(lat) * pi/2) + 90) / 180
    // y * 180 - 90 = degrees(sin(lat) * pi/2)
    // radians(y * 180 - 90) = sin(lat) * pi/2
    // sin(lat) = radians(y * 180 - 90) / (pi/2)
    const sinLat = degToRad(y * 180 - 90) / (Math.PI / 2);
    const latitude = Math.asin(sinLat);

    return [latitude, longitude];
}

/**
 * Equirectangular projection (simple lat/lon mapping)
 * @param {number} latitude - Latitude in radians
 * @param {number} longitude - Longitude in radians
 * @returns {Array<number>} [x, y] normalized to [0, 1]
 */
export function equirectangular(latitude, longitude) {
    const x = (radToDeg(longitude) + 180) / 360;
    const y = (radToDeg(latitude) + 90) / 180;
    return [x, y];
}

/**
 * Inverse equirectangular projection
 * @param {number} x - X coordinate [0, 1]
 * @param {number} y - Y coordinate [0, 1]
 * @returns {Array<number>} [latitude, longitude] in radians
 */
export function equirectangularInverse(x, y) {
    const longitude = degToRad(x * 360 - 180);
    const latitude = degToRad(y * 180 - 90);
    return [latitude, longitude];
}

/**
 * Miller cylindrical projection
 * @param {number} latitude - Latitude in radians
 * @param {number} longitude - Longitude in radians
 * @param {number} clipLat - Latitude clipping threshold (degrees)
 * @returns {Array<number>} [x, y] normalized to [0, 1]
 */
export function miller(latitude, longitude, clipLat = 0) {
    const x = (radToDeg(longitude) + 180) / 360;

    // Clip latitude
    const latMin = -(90 - clipLat) * Math.PI / 180;
    const latMax = (90 - clipLat) * Math.PI / 180;
    const y_clipped = Math.max(latMin, Math.min(latMax, latitude));

    // Apply Miller formula
    let y = (5 / 4) * Math.log(Math.tan(2 * y_clipped / 5 + Math.PI / 4));

    // Compute projection extremes for rescaling
    const y_min = (5 / 4) * Math.log(Math.tan(2 * (-degToRad(90 - clipLat)) / 5 + Math.PI / 4));
    const y_max = (5 / 4) * Math.log(Math.tan(2 * (degToRad(90 - clipLat)) / 5 + Math.PI / 4));

    // Rescale to [0, 1]
    y = (y - y_min) / (y_max - y_min);

    return [x, y];
}

/**
 * Map of projection names to functions
 */
export const PROJECTIONS = {
    mercator: mercator,
    lambert: lambert,
    equirectangular: equirectangular,
    miller: miller,
};

/**
 * Map of projection names to inverse functions
 */
export const PROJECTIONS_INVERSE = {
    mercator: mercatorInverse,
    lambert: lambertInverse,
    equirectangular: equirectangularInverse,
    // Note: Miller projection has no inverse in the Python code
};

/**
 * Aspect ratios for each projection type
 */
export const RATIOS = {
    mercator: 1 / 1.65,
    lambert: 0.5,
    equirectangular: 0.5,
    miller: 0.6,
};

// Simple array pool to avoid GC pressure (CRITICAL for performance)
const arrayPool = new Map(); // key: size, value: array of arrays

/**
 * Get array from pool or create new one
 */
function getPooledArray(size) {
    if (!arrayPool.has(size)) {
        arrayPool.set(size, []);
    }
    const pool = arrayPool.get(size);
    return pool.length > 0 ? pool.pop() : new Float32Array(size);
}

/**
 * Return array to pool for reuse
 */
export function returnArrayToPool(arr) {
    const size = arr.length;
    if (!arrayPool.has(size)) {
        arrayPool.set(size, []);
    }
    const pool = arrayPool.get(size);
    if (pool.length < 50) {  // Limit pool size
        pool.push(arr);
    }
}

/**
 * Project 3D sphere coordinates to 2D circle using orthographic projection
 * Ported from Python: svg_model.py flatten_sphere() (lines 340-366)
 *
 * @param {Float32Array} sphere3D - [x,y,z, ...] rotated 3D coordinates
 * @param {number} radius - Circle radius in pixels
 * @returns {Float32Array} [x,y, ...] screen coordinates centered at (radius, radius)
 */
export function flattenSphere(sphere3D, radius) {
    const numPoints = sphere3D.length / 3;
    const flattened = getPooledArray(numPoints * 2);  // REUSE instead of allocate!

    for (let i = 0; i < numPoints; i++) {
        const x = sphere3D[i * 3];      // Depth (front/back)
        const y = sphere3D[i * 3 + 1];  // Horizontal
        const z = sphere3D[i * 3 + 2];  // Vertical

        if (x > 0) {
            // Front-facing point: use actual y,z
            flattened[i * 2] = y;
            flattened[i * 2 + 1] = z;
        } else {
            // Back-facing point: normalize to circle edge
            const len = Math.sqrt(y * y + z * z);
            if (len > 0) {
                flattened[i * 2] = y / len;
                flattened[i * 2 + 1] = z / len;
            } else {
                flattened[i * 2] = 0;
                flattened[i * 2 + 1] = 0;
            }
        }
    }

    // Scale to radius and center at (radius, radius)
    for (let i = 0; i < numPoints * 2; i += 2) {
        flattened[i] = flattened[i] * radius + radius;
        flattened[i + 1] = flattened[i + 1] * radius + radius;
    }

    return flattened;
}
